import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useBookings } from '@/features/bookings/hooks/useRoomBooking';
import { format, addDays, subDays, isToday } from 'date-fns';
import type { Booking } from '@/features/bookings/types';

/**
 * TodayCalendarWidget Component
 * VideoGame-themed sidebar widget showing daily bookings
 */
export function TodayCalendarWidget() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAll, setShowAll] = useState(false);
  const isHebrew = i18n.language === 'he';

  // Format selected date for API
  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  // Fetch bookings for selected date
  const { data: allBookings, isLoading } = useBookings({
    startDate: formattedDate,
    endDate: formattedDate,
    status: 'PENDING,APPROVED',
  });

  const goToPreviousDay = () => {
    setSelectedDate((prev) => subDays(prev, 1));
    setShowAll(false);
  };

  const goToNextDay = () => {
    setSelectedDate((prev) => addDays(prev, 1));
    setShowAll(false);
  };

  const handleBookingClick = (bookingId: string) => {
    navigate(`/calendar/rooms/booking/${bookingId}`);
  };

  const getRoomBadge = (booking: Booking) => {
    const roomName = booking.room?.name;
    if (!roomName) return '?';
    return roomName.charAt(0).toUpperCase();
  };

  const allSortedBookings = (allBookings || []).sort((a: Booking, b: Booking) => {
    return a.startTime.localeCompare(b.startTime);
  });

  const bookings = showAll ? allSortedBookings : allSortedBookings.slice(0, 4);
  const hasMore = allSortedBookings.length > 4;
  const hasBookings = allSortedBookings.length > 0;

  const pixelFont = isHebrew
    ? 'var(--font-pixel-cards), monospace'
    : '"Handjet", monospace';
  const titleFont = isHebrew
    ? 'var(--font-pixel-titles), monospace'
    : '"Handjet", monospace';

  // Loading state
  if (isLoading) {
    return (
      <div
        className="mx-3 mb-3 rounded-md"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '4px solid var(--theme-border)',
        }}
      >
        <div className="flex items-center justify-center py-4">
          <span
            className="uppercase tracking-wider"
            style={{
              color: 'var(--theme-text-muted)',
              fontFamily: pixelFont,
              fontSize: '9px',
            }}
          >
            {t('common:loading')}
          </span>
        </div>
      </div>
    );
  }

  // Date navigation bar (shared between empty and populated states)
  const dateNav = (
    <div dir="ltr" className="flex items-center gap-1.5 mx-3 mb-2">
      <button
        onClick={goToPreviousDay}
        className="flex items-center justify-center rounded-md hover:opacity-80 transition-opacity active:translate-y-px"
        style={{
          width: '30px',
          height: '30px',
          backgroundColor: 'var(--theme-surface)',
          border: '3px solid var(--theme-border)',
          color: 'var(--theme-text)',
        }}
        aria-label={t('common:a11y.previousDay')}
      >
        <ChevronLeft size={14} />
      </button>

      <div
        className="flex items-center justify-center flex-1 rounded-md uppercase tracking-wider"
        style={{
          height: '30px',
          backgroundColor: isToday(selectedDate) ? 'var(--theme-accent)' : 'var(--theme-surface)',
          border: '3px solid var(--theme-border)',
          fontFamily: titleFont,
          fontSize: '9px',
          fontWeight: '700',
          color: 'var(--theme-text)',
          letterSpacing: '0.05em',
        }}
      >
        {isToday(selectedDate) ? t('bookings:calendar.today', 'Today') : format(selectedDate, 'MMM d')}
      </div>

      <button
        onClick={goToNextDay}
        className="flex items-center justify-center rounded-md hover:opacity-80 transition-opacity active:translate-y-px"
        style={{
          width: '30px',
          height: '30px',
          backgroundColor: 'var(--theme-surface)',
          border: '3px solid var(--theme-border)',
          color: 'var(--theme-text)',
        }}
        aria-label={t('common:a11y.nextDay')}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );

  // Empty state
  if (!hasBookings) {
    return (
      <>
        {dateNav}
        <div
          className="mx-3 mb-3 rounded-md"
          style={{
            backgroundColor: 'var(--theme-surface)',
            border: '4px solid var(--theme-border)',
          }}
        >
          <p
            className="text-center py-3 uppercase tracking-wider"
            style={{
              color: 'var(--theme-text-muted)',
              fontFamily: pixelFont,
              fontSize: '8px',
            }}
          >
            {t('bookings:calendar.noBookings', 'No bookings')}
          </p>
        </div>
      </>
    );
  }

  // Populated state
  return (
    <>
      {dateNav}

      {/* Booking list card */}
      <div
        className="mx-3 mb-3 rounded-md overflow-hidden"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '4px solid var(--theme-border)',
        }}
      >
        <div
          className="overflow-y-auto"
          style={{
            maxHeight: showAll ? '350px' : '220px',
          }}
        >
          {bookings.map((booking, i) => (
            <div
              key={booking.id}
              onClick={() => handleBookingClick(booking.id)}
              className="hover:opacity-80 transition-all cursor-pointer active:translate-y-px"
              style={{
                padding: '6px 8px',
                borderBottom: i < bookings.length - 1 ? '2px solid var(--theme-border)' : undefined,
              }}
            >
              <div className="flex items-center gap-2">
                {/* Room badge */}
                <div
                  className="flex items-center justify-center rounded-sm flex-shrink-0"
                  style={{
                    width: '22px',
                    height: '22px',
                    backgroundColor: 'var(--theme-bg)',
                    border: '2px solid var(--theme-border)',
                    fontFamily: titleFont,
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'var(--theme-text)',
                  }}
                  title={booking.room?.name || t('common:a11y.unknownRoom')}
                >
                  {getRoomBadge(booking)}
                </div>

                {/* Time & details */}
                <div className="flex-1 min-w-0">
                  <div
                    className="truncate"
                    style={{
                      fontFamily: pixelFont,
                      fontSize: '9px',
                      fontWeight: '600',
                      color: 'var(--theme-text)',
                      lineHeight: '1.3',
                    }}
                  >
                    {booking.startTime} - {booking.endTime}
                  </div>
                  <div
                    dir="auto"
                    className="truncate"
                    style={{
                      fontFamily: pixelFont,
                      fontSize: '8px',
                      color: 'var(--theme-text-muted)',
                      lineHeight: '1.3',
                    }}
                  >
                    {booking.note || booking.room?.name || 'Booking'}
                  </div>
                </div>

                {/* Status dot */}
                <div
                  className="flex-shrink-0 rounded-sm"
                  style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: booking.status === 'APPROVED' ? '#4CAF50' : '#FFA726',
                    border: '1px solid var(--theme-border)',
                  }}
                  title={booking.status}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Show more/less */}
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full hover:opacity-80 transition-opacity active:translate-y-px uppercase tracking-wider"
            style={{
              padding: '6px',
              backgroundColor: 'var(--theme-bg)',
              color: 'var(--theme-text-muted)',
              borderTop: '3px solid var(--theme-border)',
              fontFamily: pixelFont,
              fontSize: '8px',
              fontWeight: '600',
            }}
          >
            {showAll
              ? t('bookings:calendar.showLess', 'Show less')
              : `+${allSortedBookings.length - 4} ${t('bookings:calendar.more', 'more')}`}
          </button>
        )}
      </div>
    </>
  );
}
